/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { FirebaseAnalytics } from "@capacitor-community/firebase-analytics";
import { Capacitor } from '@capacitor/core';
import { logEvent, setUserId as setWebUserId } from 'firebase/analytics';

/** Bound plugin waits so hung native bridges cannot block logout/deletion. */
const ANALYTICS_CALL_TIMEOUT_MS = 2000;

@Injectable({
  providedIn: 'root'
})
export class FirebaseAnalyticsService {

  private analytics: any;
  private nativeAnalytics = FirebaseAnalytics;
  private pendingUserId: string | null | undefined;
  /** Last identity we treated as applied (skips redundant round-trips). */
  private appliedUserId: string | null | undefined;
  /**
   * Serializes setUserId/reset writes so in-flight plugin calls cannot finish
   * out of order relative to later scheduled clears.
   */
  private writeChain: Promise<void> = Promise.resolve();
  /**
   * Bumped only on authoritative clears (logout / account wipe) so a
   * registerEvent that read USER_DATA before logout cannot re-apply the old oid.
   * Best-effort null from anonymous registerEvent does not bump (avoids dropping
   * a legitimate post-login identity).
   */
  private identityGeneration = 0;

  constructor() { }

  public setAnalyticsInstance(analytics: any) {
    this.analytics = analytics;

    if (this.pendingUserId !== undefined) {
      setWebUserId(this.analytics, this.pendingUserId);
      this.appliedUserId = this.pendingUserId;
    }
  }

  /** Snapshot for callers that race Preferences.get against logout clears. */
  public getIdentityGeneration(): number {
    return this.identityGeneration;
  }

  /**
   * Assigns the app's internal, non-PII user ID to Firebase Analytics.
   *
   * @param readGeneration optional snapshot from {@link getIdentityGeneration}
   *   taken before an async USER_DATA read. Stale writes (null or oid) are dropped.
   * @param authoritative when clearing, bump generation and always hit the plugin
   *   even if the cache already says null (logout / account deletion). Needed
   *   because a timed-out native setUserId may still land after our JS gave up.
   *
   * Note: withTimeout only abandons the JS wait — it cannot cancel an already
   * dispatched native bridge call.
   */
  public setUserId = (
    userId: string | null,
    readGeneration?: number,
    authoritative = false
  ): Promise<void> => {
    const normalizedUserId = userId?.trim() || null;
    if (
      readGeneration !== undefined &&
      readGeneration !== this.identityGeneration
    ) {
      return Promise.resolve();
    }
    if (normalizedUserId === null && authoritative) {
      this.identityGeneration += 1;
    }
    const scheduledGeneration = this.identityGeneration;
    const run = () => this.applyUserId(normalizedUserId, scheduledGeneration, authoritative);
    this.writeChain = this.writeChain.then(run, run);
    return this.writeChain;
  };

  private async applyUserId(
    normalizedUserId: string | null,
    scheduledGeneration: number,
    authoritative: boolean
  ): Promise<void> {
    if (scheduledGeneration !== this.identityGeneration) {
      return;
    }

    // Skip redundant clears unless authoritative (logout may need to overwrite
    // a native id that landed after a timed-out setUserId).
    if (
      this.appliedUserId === normalizedUserId &&
      !(authoritative && normalizedUserId === null)
    ) {
      return;
    }

    if (Capacitor.isNativePlatform()) {
      if (normalizedUserId) {
        try {
          await this.withTimeout(
            this.nativeAnalytics.setUserId({ userId: normalizedUserId }),
            ANALYTICS_CALL_TIMEOUT_MS
          );
        } catch (error) {
          console.warn('[Firebase Analytics] Native setUserId failed/timed out:', error);
          throw error;
        }
        if (scheduledGeneration !== this.identityGeneration) {
          return;
        }
        this.appliedUserId = normalizedUserId;
        return;
      }

      try {
        await this.withTimeout(
          this.nativeAnalytics.setUserId({
            userId: null as unknown as string,
          }),
          ANALYTICS_CALL_TIMEOUT_MS
        );
        if (scheduledGeneration === this.identityGeneration) {
          this.appliedUserId = null;
        }
        return;
      } catch (error) {
        try {
          await this.withTimeout(
            this.nativeAnalytics.setUserId({ userId: '' }),
            ANALYTICS_CALL_TIMEOUT_MS
          );
          if (scheduledGeneration === this.identityGeneration) {
            this.appliedUserId = null;
          }
          return;
        } catch (emptyError) {
          console.warn(
            '[Firebase Analytics] Native clear failed; leaving appliedUserId unchanged for retry:',
            error,
            emptyError
          );
          return;
        }
      }
    }

    if (scheduledGeneration !== this.identityGeneration) {
      return;
    }

    this.pendingUserId = normalizedUserId;
    if (this.analytics) {
      setWebUserId(this.analytics, normalizedUserId);
    }
    this.appliedUserId = normalizedUserId;
  }

  /**
   * Clears all analytics data and regenerates the app instance id.
   * Use only on permanent account deletion — not on logout or anonymous sessions.
   */
  public resetAnalyticsData = (): Promise<void> => {
    this.identityGeneration += 1;
    const scheduledGeneration = this.identityGeneration;
    const run = () => this.applyResetAnalyticsData(scheduledGeneration);
    this.writeChain = this.writeChain.then(run, run);
    return this.writeChain;
  };

  private async applyResetAnalyticsData(scheduledGeneration: number): Promise<void> {
    try {
      if (Capacitor.isNativePlatform()) {
        await this.withTimeout(this.nativeAnalytics.reset(), ANALYTICS_CALL_TIMEOUT_MS);
      } else {
        this.pendingUserId = null;
        if (this.analytics) {
          setWebUserId(this.analytics, null);
        }
      }
    } finally {
      if (scheduledGeneration === this.identityGeneration) {
        this.appliedUserId = null;
      }
    }
  }

  public setAnalyticsEvent = async (eventName: string, eventProperties: any) => {
    if (Capacitor.isNativePlatform()) {
      await FirebaseAnalytics.logEvent({
        name: eventName,
        params: eventProperties,
      });
    } else if (this.analytics) {
      logEvent(this.analytics, eventName, eventProperties);
    }
  };

  private withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error(`Firebase Analytics call timed out after ${ms}ms`));
      }, ms);
      promise.then(
        (value) => {
          clearTimeout(timer);
          resolve(value);
        },
        (error) => {
          clearTimeout(timer);
          reject(error);
        }
      );
    });
  }
}
